/**
 * Firestore Proxy Service
 *
 * This service provides a proxy layer for Firestore operations through n8n webhooks.
 * All data access is routed through the Data Access Cloud Function via n8n workflows.
 *
 * Benefits:
 * - Centralized data access control
 * - Audit logging through n8n
 * - Reduced direct Firestore SDK usage in frontend
 * - Easier migration to other backends if needed
 */

import { auth } from './firebase'
import type { DeviceRecord } from './device-fingerprint'

// BFF Service - Backend for Frontend (for other endpoints)
const BFF_URL = import.meta.env.VITE_BFF_URL || 'https://bff-service.lumenframework.com'

const DATA_ACCESS_CONFIG = {
  baseUrl: BFF_URL,  // Route through BFF
  bffUrl: BFF_URL,  // For endpoints that work on BFF
  endpoints: {
    testCases: '/test-cases',  // Data Access endpoint
    executions: '/executions',  // Data Access endpoint
    expertSessions: '/expert-sessions', // Endpoint for expert evaluation sessions
    metricsSummary: '/metrics/summary',  // Data Access endpoint
    health: '/health',  // Data Access endpoint
  },
  timeout: 30000,
  retry: {
    maxAttempts: 3,
    baseDelay: 1000,
  },
}

interface ProxyResponse<T> {
  success: boolean
  data?: T
  error?: string
  count?: number
}

// Public API key for expert evaluation (unauthenticated access)
// Reads from environment variable, set at build time
const PUBLIC_API_KEY = import.meta.env.VITE_PUBLIC_API_KEY || 'lumen-expert-public-2024'

async function getAuthToken(): Promise<string | null> {
  const user = auth.currentUser
  if (!user) return null
  return user.getIdToken()
}

async function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

async function proxyRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  retryCount = 0
): Promise<ProxyResponse<T>> {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), DATA_ACCESS_CONFIG.timeout)

  try {
    const token = await getAuthToken()
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    }

    const url = `${DATA_ACCESS_CONFIG.baseUrl}${endpoint}`

    const response = await fetch(url, {
      ...options,
      headers,
      signal: controller.signal,
    })

    clearTimeout(timeoutId)

    if (!response.ok) {
      throw new Error(`HTTP error: ${response.status}`)
    }

    const data = await response.json()
    return { success: true, data: data.data ?? data, count: data.count }
  } catch (error) {
    clearTimeout(timeoutId)

    // Retry logic for network errors
    if (retryCount < DATA_ACCESS_CONFIG.retry.maxAttempts) {
      const delay = DATA_ACCESS_CONFIG.retry.baseDelay * Math.pow(2, retryCount)
      await sleep(delay)
      return proxyRequest<T>(endpoint, options, retryCount + 1)
    }

    const message = error instanceof Error ? error.message : 'Unknown error'
    return { success: false, error: message }
  }
}

// ============================================
// Test Cases
// ============================================

export interface TestCase {
  case_id: string
  profile: {
    profile_id: string
    role: string
    tech_proficiency: string
    accessibility_needs: string
    language_level: string
  }
  task_type: string
  process_phase: string
  process_data: {
    process_number: string
    phase: string
    last_movement: string
  }
}

export interface TestCaseWithId extends TestCase {
  id: string
  created_at?: string
  updated_at?: string
}

export async function getTestCases(filters?: {
  persona?: string
  taskType?: string
  phase?: string
  limit?: number
}): Promise<ProxyResponse<TestCaseWithId[]>> {
  const params = new URLSearchParams()
  if (filters?.persona) params.append('persona', filters.persona)
  if (filters?.taskType) params.append('task_type', filters.taskType)
  if (filters?.phase) params.append('phase', filters.phase)
  if (filters?.limit) params.append('limit', filters.limit.toString())

  const queryString = params.toString()
  const endpoint = queryString
    ? `${DATA_ACCESS_CONFIG.endpoints.testCases}?${queryString}`
    : DATA_ACCESS_CONFIG.endpoints.testCases

  return proxyRequest<TestCaseWithId[]>(endpoint)
}

export async function getTestCase(caseId: string): Promise<ProxyResponse<TestCaseWithId>> {
  return proxyRequest<TestCaseWithId>(`${DATA_ACCESS_CONFIG.endpoints.testCases}/${caseId}`)
}

export async function createTestCase(testCase: TestCase): Promise<ProxyResponse<TestCaseWithId>> {
  return proxyRequest<TestCaseWithId>(DATA_ACCESS_CONFIG.endpoints.testCases, {
    method: 'POST',
    body: JSON.stringify(testCase),
  })
}

// ============================================
// Executions
// ============================================

export interface Execution {
  execution_id: string
  case_id: string
  status: 'pending' | 'running' | 'success' | 'failed' | 'error'
  ui_spec?: string | object  // Generated UI specification JSON
  profile_id?: string
  task_type?: string
  process_phase?: string
  tads?: number
  retry_count?: number
  started_at?: string
  timestamp?: string
  metrics?: {
    tads: number
    retry_count: number
    flesch_index: number
    legal_term_ratio: number
    information_density: number
    component_count: number
    tree_depth: number
    execution_time_ms: number
  }
  error_message?: string
  created_at?: string
  completed_at?: string
}


export interface ExecutionWithId extends Execution {
  id: string
}

export async function getExecutions(filters?: {
  caseId?: string
  status?: string
  limit?: number
  orderBy?: string
}): Promise<ProxyResponse<ExecutionWithId[]>> {
  const params = new URLSearchParams()
  if (filters?.caseId) params.append('case_id', filters.caseId)
  if (filters?.status) params.append('status', filters.status)
  if (filters?.limit) params.append('limit', filters.limit.toString())
  if (filters?.orderBy) params.append('order_by', filters.orderBy)

  const queryString = params.toString()
  const endpoint = queryString
    ? `${DATA_ACCESS_CONFIG.endpoints.executions}?${queryString}`
    : DATA_ACCESS_CONFIG.endpoints.executions

  return proxyRequest<ExecutionWithId[]>(endpoint)
}

/**
 * Get a single execution by ID.
 * Uses API key for unauthenticated access (for preview pages in new tabs).
 */
export async function getExecution(executionId: string): Promise<ProxyResponse<ExecutionWithId>> {
  const token = await getAuthToken()

  // Use API key if no auth token (for preview pages opened in new tabs)
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : { 'X-API-Key': PUBLIC_API_KEY }),
  }

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), DATA_ACCESS_CONFIG.timeout)

  try {
    const url = `${DATA_ACCESS_CONFIG.baseUrl}${DATA_ACCESS_CONFIG.endpoints.executions}/${executionId}`

    const response = await fetch(url, {
      method: 'GET',
      headers,
      signal: controller.signal,
    })

    clearTimeout(timeoutId)

    if (!response.ok) {
      throw new Error(`HTTP error: ${response.status}`)
    }

    const data = await response.json()
    return { success: true, data: data.data ?? data }
  } catch (error) {
    clearTimeout(timeoutId)
    const message = error instanceof Error ? error.message : 'Unknown error'
    console.error('Error fetching execution:', message)
    return { success: false, error: message }
  }
}

// Interface for executions returned by /executions/successful
export interface SuccessfulExecution {
  execution_id: string
  case_id: string
  profile_id: string
  task_type: string
  process_phase: string
  ui_spec: string | object
  ui_spec_pt?: string | object
  ui_spec_en?: string | object
  timestamp?: string
}

/**
 * Get successful executions with ui_spec for expert evaluation.
 * Uses API key for unauthenticated access (expert evaluators).
 */
export async function getSuccessfulExecutions(limit = 32): Promise<ProxyResponse<SuccessfulExecution[]>> {
  const token = await getAuthToken()

  // Use API key if no auth token (for expert evaluators)
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : { 'X-API-Key': PUBLIC_API_KEY }),
  }

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), DATA_ACCESS_CONFIG.timeout)

  try {
    const url = `${DATA_ACCESS_CONFIG.baseUrl}${DATA_ACCESS_CONFIG.endpoints.executions}/successful?limit=${limit}`

    const response = await fetch(url, {
      method: 'GET',
      headers,
      signal: controller.signal,
    })

    clearTimeout(timeoutId)

    if (!response.ok) {
      throw new Error(`HTTP error: ${response.status}`)
    }

    const data = await response.json()
    return { success: true, data: data.data ?? data, count: data.count }
  } catch (error) {
    clearTimeout(timeoutId)
    const message = error instanceof Error ? error.message : 'Unknown error'
    console.error('Error fetching successful executions:', message)
    return { success: false, error: message }
  }
}

// ============================================
// Metrics
// ============================================

export interface MetricsSummary {
  total_executions: number
  success_rate: number
  avg_tads: number
  avg_execution_time: number
  by_persona: Record<string, {
    total: number
    success_rate: number
    avg_tads: number
  }>
  by_task_type: Record<string, {
    total: number
    success_rate: number
  }>
  generated_at: string
}

export async function getMetricsSummary(): Promise<ProxyResponse<MetricsSummary>> {
  return proxyRequest<MetricsSummary>(DATA_ACCESS_CONFIG.endpoints.metricsSummary)
}

// ============================================
// Expert Sessions
// ============================================

// Expert evaluation ratings interface
export interface IQAIRatings {
  clarity: number
  adequacy: number
  consistency: number
  accessibility: number
  usability: number
}

// Interaction metadata for each evaluation
export interface InteractionMetadata {
  tabs_viewed: string[]
  tab_times: {
    persona: number
    task: number
    interface: number
  }
  scroll_depth_percent: number
  click_count: number
  rating_changes: Array<{
    dimension: string
    from: number
    to: number
    at: string
  }>
  dimension_order: string[]
  zoom_level: number
  fullscreen_used: boolean
}

// Individual evaluation for an interface
export interface IQAIEvaluation {
  execution_id: string
  profile_id: string
  task_type: string
  process_phase: string
  ratings: IQAIRatings
  iqai_score: number
  comments?: string
  evaluated_at: string
  time_spent_seconds: number
  interaction_metadata?: InteractionMetadata
}

// Session quality indicators
export interface SessionQualityIndicators {
  has_comments: boolean
  rating_variance: number
  avg_time_adequate: boolean
}

// Aggregated session metrics
export interface SessionMetrics {
  total_time_seconds: number
  avg_time_per_interface: number
  breaks_count: number
  completion_rate: number
  devices_count: number
  quality_indicators: SessionQualityIndicators
}

// Expert profile interface (flexible to accommodate different form schemas)
export interface ExpertProfile {
  // From welcome form
  expertise?: string
  other_expertise?: string
  years_experience?: string
  gender?: string
  age_range?: string
  accessibility_need?: string
  other_accessibility?: string
  education?: string
  is_qualified?: boolean
  // Optional legacy fields
  expertise_area?: string[]
  familiarity_govbr?: number
  familiarity_legal?: number
  accessibility_needs?: string[]
  name?: string
  email?: string
}

// Full expert session interface
export interface ExpertSession {
  expert_id: string
  recovery_code: string
  devices: DeviceRecord[]
  profile: ExpertProfile
  interfaces_to_evaluate: Array<{
    execution_id: string
    profile_id: string
    task_type: string
    process_phase: string
    ui_spec: object
  }>
  current_index: number
  evaluations: IQAIEvaluation[]
  status: 'in_progress' | 'completed' | 'abandoned'
  started_at: string
  last_activity_at: string
  completed_at?: string
  session_metrics?: SessionMetrics
  metadata: {
    app_version: string
    created_from: 'new' | 'recovered' | 'provisional'
    recovery_count: number
    last_device_fingerprint: string
  }
}

/**
 * Saves an expert session to Firestore.
 * The recovery code is used as the document ID.
 */
export async function saveExpertSession(session: ExpertSession): Promise<ProxyResponse<{ id: string }>> {
  return proxyRequest<{ id: string }>(
    `${DATA_ACCESS_CONFIG.endpoints.expertSessions}/${session.recovery_code}`, {
    method: 'PUT',
    body: JSON.stringify(session),
  });
}

/**
 * Retrieves an expert session from Firestore using the recovery code.
 * Uses API key for unauthenticated access.
 */
export async function getExpertSessionByCode(recoveryCode: string): Promise<ProxyResponse<ExpertSession>> {
  const token = await getAuthToken()

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : { 'X-API-Key': PUBLIC_API_KEY }),
  }

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), DATA_ACCESS_CONFIG.timeout)

  try {
    const url = `${DATA_ACCESS_CONFIG.baseUrl}${DATA_ACCESS_CONFIG.endpoints.expertSessions}/${recoveryCode}`

    const response = await fetch(url, {
      method: 'GET',
      headers,
      signal: controller.signal,
    })

    clearTimeout(timeoutId)

    if (!response.ok) {
      if (response.status === 404) {
        return { success: false, error: 'Session not found' }
      }
      throw new Error(`HTTP error: ${response.status}`)
    }

    const data = await response.json()
    return { success: true, data: data.data ?? data }
  } catch (error) {
    clearTimeout(timeoutId)
    const message = error instanceof Error ? error.message : 'Unknown error'
    console.error('Error fetching expert session:', message)
    return { success: false, error: message }
  }
}

/**
 * Get all expert sessions (admin only).
 * Returns all sessions with their evaluations for the admin dashboard.
 */
export async function getAllExpertSessions(): Promise<ProxyResponse<ExpertSession[]>> {
  return proxyRequest<ExpertSession[]>(`${DATA_ACCESS_CONFIG.endpoints.expertSessions}`)
}

/**
 * Get expert session by device fingerprint.
 * Used for automatic session recovery on the same device.
 */
export async function getExpertSessionByDevice(fingerprint: string): Promise<ProxyResponse<ExpertSession | null>> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'X-API-Key': PUBLIC_API_KEY,
  }

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), DATA_ACCESS_CONFIG.timeout)

  try {
    const url = `${DATA_ACCESS_CONFIG.baseUrl}${DATA_ACCESS_CONFIG.endpoints.expertSessions}/by-device/${fingerprint}`

    const response = await fetch(url, {
      method: 'GET',
      headers,
      signal: controller.signal,
    })

    clearTimeout(timeoutId)

    if (!response.ok) {
      if (response.status === 404) {
        return { success: true, data: null } // No session for this device
      }
      throw new Error(`HTTP error: ${response.status}`)
    }

    const data = await response.json()
    return { success: true, data: data.data ?? data }
  } catch (error) {
    clearTimeout(timeoutId)
    const message = error instanceof Error ? error.message : 'Unknown error'
    console.error('Error fetching session by device:', message)
    return { success: false, error: message }
  }
}

/**
 * Update session activity timestamp.
 * Called periodically to track when user was last active.
 */
export async function updateSessionActivity(recoveryCode: string): Promise<ProxyResponse<void>> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'X-API-Key': PUBLIC_API_KEY,
  }

  try {
    const url = `${DATA_ACCESS_CONFIG.baseUrl}${DATA_ACCESS_CONFIG.endpoints.expertSessions}/${recoveryCode}/activity`

    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ last_activity_at: new Date().toISOString() }),
    })

    if (!response.ok) {
      throw new Error(`HTTP error: ${response.status}`)
    }

    return { success: true }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error'
    console.error('Error updating session activity:', message)
    return { success: false, error: message }
  }
}

/**
 * Save a single evaluation to an expert session.
 * Used for incremental auto-save as user evaluates each interface.
 */
export async function saveEvaluation(
  recoveryCode: string,
  evaluationIndex: number,
  evaluation: IQAIEvaluation
): Promise<ProxyResponse<void>> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'X-API-Key': PUBLIC_API_KEY,
  }

  try {
    const url = `${DATA_ACCESS_CONFIG.baseUrl}${DATA_ACCESS_CONFIG.endpoints.expertSessions}/${recoveryCode}/evaluations/${evaluationIndex}`

    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify(evaluation),
    })

    if (!response.ok) {
      throw new Error(`HTTP error: ${response.status}`)
    }

    return { success: true }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error'
    console.error('Error saving evaluation:', message)
    return { success: false, error: message }
  }
}

/**
 * Calculate IQAI score from ratings.
 * Formula: IQAI = ((CL + AD + CO + AC + US) - 5) / 20 * 100
 * Returns percentage 0-100
 */
export function calculateIQAI(ratings: IQAIRatings): number {
  const { clarity, adequacy, consistency, accessibility, usability } = ratings
  const sum = clarity + adequacy + consistency + accessibility + usability
  return Math.round(((sum - 5) / 20) * 100)
}

// ============================================
// Health Check
// ============================================

export async function checkHealth(): Promise<ProxyResponse<{ status: string; timestamp: string }>> {
  return proxyRequest<{ status: string; timestamp: string }>(DATA_ACCESS_CONFIG.endpoints.health)
}

// ============================================
// Real-time Simulation (Polling)
// ============================================

export interface RealtimeSubscription {
  unsubscribe: () => void
}

/**
 * Simulates real-time updates by polling the API.
 * In production, consider using Server-Sent Events or WebSockets.
 */
export function subscribeToExecutions(
  callback: (executions: ExecutionWithId[]) => void,
  options?: {
    limit?: number
    pollInterval?: number
  }
): RealtimeSubscription {
  const pollInterval = options?.pollInterval || 5000
  const limit = options?.limit || 100

  let isActive = true

  const poll = async () => {
    if (!isActive) return

    const result = await getExecutions({ limit, orderBy: 'created_at_desc' })
    if (result.success && result.data) {
      callback(result.data)
    }

    if (isActive) {
      setTimeout(poll, pollInterval)
    }
  }

  // Start polling
  poll()

  return {
    unsubscribe: () => {
      isActive = false
    },
  }
}

export { DATA_ACCESS_CONFIG }
